import { addMinutes, subMilliseconds } from 'date-fns';

import { parseLogDate, parseLogKeyData } from '@trqk/utils';
import { s3Queries } from '@trqk/resolvers';

const { listObjectsInS3 } = s3Queries;

/**
 * @typedef {GetLogEventsInput}
 * @property {string} bucketName Name of the target S3 bucket
 * @property {string} bucketRegion AWS region where the bucket is hosted
 * @property {string} identityPoolId AWS Cognito identity pool ID
 * @property {number} lookback a date offset in milliseconds. This is subtracted from the current
 * time to get the start date after which logEvents will be included in the response.
 * @property {Maybe<string>} startAfter the log key to use as the continuation token in query for
 * events in the S3 bucket
 * @property {string} userId current user ID
 */
interface GetLogEventsInput {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    lookback: number;
    startAfter: Maybe<string>;
    userId: string;
}

/**
 * Get a subset of log events for a given userId.
 * @param obj
 * @param {GetLogEventsInput} input
 */
export const getLogEvents = async ({
    bucketName,
    bucketRegion,
    identityPoolId,
    lookback,
    userId,
    startAfter = null,
}: GetLogEventsInput): Promise<LogEvent[]> => {
    if (
        !bucketName ||
        !bucketRegion ||
        !identityPoolId ||
        !lookback ||
        typeof lookback !== 'number' ||
        !userId
    ) {
        return [] as LogEvent[];
    }

    let logEvents: LogEvent[] = [];

    // startAfter is stored in localStorage by packages/hooks/src/useLogData/LogDataProvider.tsx.
    // This ensures that we can get access to it and use it as a continuation token after the first
    // time we run this getLogEvents() for a given user. If we have not yet run getLogEvents() for
    // a user, we will have no startAfter token, and all the keys in their log bucket will be
    // fetched.
    const { objectKeys } = await listObjectsInS3(
        {},
        {
            bucketName,
            bucketRegion,
            identityPoolId,
            listAll: true,
            prefix: userId,
            startAfter: startAfter ?? undefined,
        },
    );

    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset();
    const nowWithTimezoneOffset = addMinutes(now, timezoneOffset);
    const startDate = subMilliseconds(nowWithTimezoneOffset, lookback);

    // We filter out keys before the lookback period here so that we are not flooding
    // LogDataProvider with old log events. LogDataProvider will call handleLogDetails() for each
    // log event, which in turn downloads the JSON data for any log event that has a handler, and
    // we don't want to download more data from S3 than we have to.
    const keysAfterStartDate = objectKeys.filter((key: string): boolean => {
        const timestamp = key.split('/')[1].slice(0, 15);
        const logDate = parseLogDate(timestamp);

        if (logDate > startDate) {
            return true;
        }

        return false;
    });

    logEvents = keysAfterStartDate.map(
        (key: string): LogEvent => parseLogKeyData(key),
    );

    return logEvents;
};
